import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar } from "@/components/Avatar";
import { PRESET_AVATARS } from "@/lib/avatars";
import { Upload, Check, Trash2, FolderKanban } from "lucide-react";
import { toast } from "sonner";

/**
 * ProfileEditDialog — lets a user edit their display name, avatar and
 * the currently-assigned project. On save, PATCHes /users/me/profile.
 */
export function ProfileEditDialog({ open, onOpenChange, user, onSaved, initialTab = "profile" }) {
  const fileRef = useRef(null);
  const [name, setName] = useState(user?.name || "");
  const [alias, setAlias] = useState(user?.alias || "");
  const [photo, setPhoto] = useState(user?.photo || "");
  const [projectId, setProjectId] = useState(user?.current_project_id || "");
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(user?.name || "");
    setAlias(user?.alias || "");
    setPhoto(user?.photo || "");
    setProjectId(user?.current_project_id || "");
    api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
  }, [open, user?.name, user?.alias, user?.photo, user?.current_project_id]);

  const displayNameValue = alias || name;

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Solo se permiten imágenes");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhoto(fileUrl(data.path));
      toast.success("Foto lista para guardar.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No fue posible subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const clearPhoto = () => setPhoto("");

  const save = async () => {
    const cleanName = name.trim();
    if (!cleanName) { toast.error("Escribe tu nombre"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me/profile", {
        name: cleanName,
        alias: alias.trim(),
        photo: photo || "",
        current_project_id: projectId || "",
      });
      toast.success("Perfil actualizado.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No fue posible actualizar el perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="profile-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Modificar datos de tu perfil</DialogTitle>
        </DialogHeader>

        {/* Current avatar preview */}
        <div className="flex flex-col items-center py-2">
          <Avatar name={displayNameValue || "?"} photo={photo} size={96} className="shadow-md" />
          {photo && (
            <button
              type="button"
              onClick={clearPhoto}
              className="text-[10px] uppercase tracking-[0.15em] text-[#E11D48] hover:text-[#9F1239] mt-2 inline-flex items-center gap-1"
              data-testid="profile-clear-photo"
            >
              <Trash2 size={11} /> Quitar foto
            </button>
          )}
        </div>

        {/* Name fields */}
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nombre completo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm h-10" data-testid="profile-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nombre visible (alias)</Label>
            <Input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Cómo quieres que te vean en la app" className="rounded-sm h-10" data-testid="profile-alias-input" />
            <p className="text-[11px] text-[#5C6A7D]">Si lo dejas vacío, se mostrará tu nombre completo.</p>
          </div>
        </div>

        {/* Project self-assignment */}
        <div className="space-y-1.5 pt-2 border-t border-[#E2E8F0]">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D] flex items-center gap-1.5"><FolderKanban size={12} /> Proyecto asignado</Label>
          <Select value={projectId || "__none__"} onValueChange={(v) => setProjectId(v === "__none__" ? "" : v)}>
            <SelectTrigger className="rounded-sm h-10" data-testid="profile-project-select"><SelectValue placeholder="Selecciona tu proyecto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Sin proyecto asignado —</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-[#5C6A7D]">Solo puedes cargar activos y fotos al proyecto que elijas aquí. Puedes cambiarlo cuando quieras.</p>
        </div>

        {/* Preset avatars */}
        <div className="space-y-2 pt-2 border-t border-[#E2E8F0]">
          <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Elige un avatar</Label>
          <div className="grid grid-cols-5 gap-3" data-testid="preset-avatars">
            {PRESET_AVATARS.map((p) => {
              const selected = photo === p.url;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPhoto(p.url)}
                  className={`relative aspect-square rounded-full overflow-hidden border-2 transition-all ${selected ? "border-[#10B981] ring-2 ring-[#10B981]/30" : "border-[#E2E8F0] hover:border-[#0A2540]/40"}`}
                  data-testid={`preset-avatar-${p.key.split(":")[1]}`}
                  aria-label={p.label}
                >
                  <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                  {selected && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
                      <Check size={20} strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Or upload own photo */}
        <div className="pt-2 border-t border-[#E2E8F0]">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0])}
            data-testid="profile-photo-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-sm w-full"
            data-testid="profile-photo-upload"
          >
            <Upload size={14} className="mr-2" />
            {uploading ? "Subiendo…" : "O sube tu propia foto"}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-sm" data-testid="profile-save">
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
